import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Portal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TextField,
    Autocomplete,
    Checkbox,
    CircularProgress,
    Tooltip,
    Popper,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DocumentService from '../../../Services/documents.service';
import ImportExportService from '../../../Services/importExport.service';
import EnumsService from '../../../Services/enums.service';
import SalariesService, { SALARY_SOURCE } from '../../../Services/salaries.service';
import Constants from '../../../assests/Constants/constants';
import { JournalEntryTypes } from '../../../assests/Constants/journalEntryTypes';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import MoveinDatImportModal from '../../ClientDetails/SubComponents/ClientDetailsHeader/MoveinDatImportModal';
import fieldStyles from './AddJournalEntryModal.module.scss';
import modalStyles from './JournalEntryTemplatesModal.module.scss';
import styles from './CompoundJournalModals.module.scss';

const TAB_MANUAL = 'manual';
const TAB_IMPORT = 'import';

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => (
    <Popper
        {...props}
        ref={ref}
        dir={direction}
        className={`${props.className || ''} ${fieldStyles.comboPopper}`}
        placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
        style={{ ...props.style, zIndex: 1600 }}
    />
));
ComboPopper.displayName = 'ComboPopper';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const toUtcNoonIso = (d) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12)).toISOString();

let lineSeq = 0;
const emptyLine = () => ({ key: ++lineSeq, account: '', amount: '', description: '' });

/** Single-sided payroll lines; `settingsKey` is the salary settings category whose sort code is the default. */
const PAYROLL_CATEGORIES = [
    { key: 'salaries', labelKey: 'PayrollCategorySalary', settingsKey: 'salaries' },
    { key: 'nationalInsurance', labelKey: 'NationalInsurance' },
    { key: 'socialBenefits', labelKey: 'SalarySocialBenefits', settingsKey: 'socialBenefits' },
];
const emptyCategoryLines = () => PAYROLL_CATEGORIES.map(({ key }) => ({
    key, enabled: true, sortCodeId: '', amount: '', description: '',
}));

const PayrollJournalModal = ({
    open,
    onClose,
    client,
    token,
    direction = 'rtl',
    accountCardOptions = [],
    sortCodeIdOptions = [],
    settings,
    fetchEnums,
    defaultDate,
    onCreated,
}) => {
    const { t } = useTranslation();
    const textAlign = direction === 'rtl' ? 'right' : 'left';
    const inputProps = useMemo(() => ({ style: { textAlign } }), [textAlign]);
    const comboInputProps = useMemo(() => ({ disableUnderline: true }), []);
    const renderComboPopper = useCallback(
        (props) => <ComboPopper {...props} direction={direction} />,
        [direction]
    );

    const [tab, setTab] = useState(TAB_MANUAL);
    const [importOpen, setImportOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const [documentDate, setDocumentDate] = useState(null);
    const [reportingMonth, setReportingMonth] = useState(null);
    const [reference, setReference] = useState('');
    const [description, setDescription] = useState('');
    const [debitLines, setDebitLines] = useState([emptyLine()]);
    const [creditLines, setCreditLines] = useState([emptyLine()]);
    const [categoryLines, setCategoryLines] = useState(emptyCategoryLines);

    const isSingleSided = client?.accountManagement?.trim() === Constants.AccountManagementOptions[1].value;

    useEffect(() => {
        if (!open) return;
        const base = defaultDate ? new Date(defaultDate) : new Date();
        const year = base.getFullYear();
        const month = base.getMonth();
        setTab(TAB_MANUAL);
        setImportOpen(false);
        setDocumentDate(new Date(Date.UTC(year, month + 1, 0)));
        setReportingMonth(new Date(Date.UTC(year, month, 1)));
        setReference('');
        setDescription(t('PayrollJournal') || 'פקודות שכר');
        setDebitLines([emptyLine()]);
        setCreditLines([emptyLine()]);
        setCategoryLines(emptyCategoryLines());
    }, [open, defaultDate, t]);

    useEffect(() => {
        if (!open || !isSingleSided || !client?.caseId || !token) return undefined;
        let cancelled = false;
        SalariesService.getSettings({ caseId: client.caseId }, token)
            .then((salarySettings) => {
                if (cancelled) return;
                const defaults = Object.fromEntries(PAYROLL_CATEGORIES.map(({ key, settingsKey }) => {
                    const source = settingsKey ? salarySettings?.[settingsKey] : null;
                    const isSortCodes = source?.sourceType === SALARY_SOURCE.SORT_CODES;
                    return [key, isSortCodes && source.codes?.length ? String(source.codes[0]) : ''];
                }));
                setCategoryLines((prev) => prev.map((l) => (
                    l.sortCodeId || !defaults[l.key] ? l : { ...l, sortCodeId: defaults[l.key] }
                )));
            })
            .catch((error) => console.error('Error loading salary settings:', error));
        return () => { cancelled = true; };
    }, [open, isSingleSided, client?.caseId, token]);

    const cardOptions = useMemo(() => (
        (accountCardOptions || [])
            .filter((c) => c && c.cardId != null && c.cardId !== '')
            .map((c) => ({ value: String(c.cardId), label: `${c.cardId} - ${c.name || ''}` }))
    ), [accountCardOptions]);

    const sortCodeOptions = useMemo(() => (
        (sortCodeIdOptions || [])
            .filter((sc) => sc && sc.sortCodeId != null && sc.sortCodeId !== '')
            .map((sc) => ({ value: String(sc.sortCodeId), label: `${sc.sortCodeId} - ${sc.sortCodeName || ''}` }))
    ), [sortCodeIdOptions]);

    const formattedSortCodeVatType = useMemo(() => (
        Object.entries(Constants.SortCodeVatType || {}).map(([key, value]) => ({
            name: t(value),
            value: key,
            label: t(value),
        }))
    ), [t]);

    const createMissingSortCodes = useCallback(async (codes) => {
        if (!codes?.length || !client) return;
        const results = await Promise.all(codes.map((sc) => {
            const parsedVatType = sc.sortCodeVatType != null && sc.sortCodeVatType !== ''
                ? parseInt(sc.sortCodeVatType, 10)
                : null;
            return EnumsService.AddSortCode(
                client.accountantId,
                sc.sortCodeName,
                sc.sortCodeId,
                sc.sortCodeGroup ?? null,
                null,
                null,
                Number.isNaN(parsedVatType) ? null : parsedVatType,
                sc.vat,
                sc.participatesInProfitAndLoss,
                null,
                null,
                0,
                token
            );
        }));
        if (results.some((r) => !r || !r.ok)) {
            toast.error(t('CreateSortCodesFailed'));
            throw new Error('Sort code creation failed');
        }
        toast.success(t('MissingSortCodesCreated'));
    }, [client, token, t]);

    const sumLines = (lines) => round2(lines.reduce((s, l) => s + round2(l.amount), 0));
    const totalDebit = sumLines(debitLines);
    const totalCredit = sumLines(creditLines);
    const difference = round2(totalDebit - totalCredit);
    const isBalanced = difference === 0;
    const linesValid = [...debitLines, ...creditLines].every((l) => l.account && round2(l.amount) !== 0);
    const activeCategoryLines = categoryLines.filter((l) => l.enabled);
    const totalCategories = sumLines(activeCategoryLines);
    const categoryLinesValid = activeCategoryLines.length > 0
        && activeCategoryLines.every((l) => l.sortCodeId && round2(l.amount) !== 0);
    const entryValid = isSingleSided ? categoryLinesValid : isBalanced && totalDebit > 0 && linesValid;
    const canCreate = entryValid && !!documentDate && !!reportingMonth && !saving;

    const categoryLabel = (key) => t(PAYROLL_CATEGORIES.find((c) => c.key === key)?.labelKey);
    const updateCategoryLine = (key, field, value) =>
        setCategoryLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));

    const updateLine = (setLines) => (key, field, value) =>
        setLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
    const removeLine = (setLines) => (key) =>
        setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== key) : prev));

    const handleCreate = async () => {
        if (!canCreate) return;
        const reportingIso = new Date(Date.UTC(
            reportingMonth.getUTCFullYear(), reportingMonth.getUTCMonth(), 10, 12
        )).toISOString();
        const base = {
            caseId: client.caseId,
            accountantId: client.accountantId,
            reference,
            documentDate: toUtcNoonIso(documentDate),
            uploadDate: new Date().toISOString(),
            reportingMonthDate: reportingIso,
            registrationMonthDate: reportingIso,
            vat: 0,
            journalEntryType: JournalEntryTypes.PayrollJournal,
        };
        const rows = isSingleSided ? activeCategoryLines.map((l) => ({
            ...base,
            description: l.description || `${description} - ${categoryLabel(l.key)}`,
            debitAccount: '',
            creditAccount: '',
            sortCodeId: l.sortCodeId,
            amount: round2(l.amount),
        })) : [
            ...debitLines.map((l) => ({
                ...base,
                description: l.description || description,
                debitAccount: l.account,
                creditAccount: '',
                sortCodeId: String(settings?.sortCodeDebit ?? '1'),
                amount: round2(l.amount),
            })),
            ...creditLines.map((l) => ({
                ...base,
                description: l.description || description,
                debitAccount: '',
                creditAccount: l.account,
                sortCodeId: String(settings?.sortCodeCredit ?? '2'),
                amount: round2(l.amount),
            })),
        ];

        setSaving(true);
        try {
            const res = await DocumentService.UploadDocumentPayloads('JournalEntries', rows, token);
            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || 'Create failed');
            }
            toast.success(t('PayrollJournalCreated') || 'Payroll journal entry created');
            onClose();
            if (onCreated) await onCreated();
        } catch (err) {
            toast.error(err?.message || t('PayrollJournalCreateFailed') || 'Failed to create payroll journal entry');
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    if (importOpen) {
        return (
            <MoveinDatImportModal
                open
                onClose={() => setImportOpen(false)}
                caseId={client?.caseId}
                token={token}
                previewMovinDat={ImportExportService.PreviewMovinDat}
                commitMovinDat={ImportExportService.ImportTransactionReportMovinDat}
                onSuccess={async () => {
                    onClose();
                    if (onCreated) await onCreated();
                }}
                t={t}
                direction={direction}
                sortCodeOptions={sortCodeIdOptions}
                accountCardOptions={accountCardOptions}
                fetchEnums={fetchEnums}
                createMissingSortCodes={createMissingSortCodes}
                formattedSortCodeVatType={formattedSortCodeVatType}
                formattedSortCodesGroupNamesOptions={[]}
                Constants={Constants}
                journalEntryType={JournalEntryTypes.PayrollJournal}
                requireBalanced
                title={`${t('PayrollJournalEntries') || 'פקודות שכר'} – ${t('ImportFromMovinDat') || 'Import MovinDAT'}`}
            />
        );
    }

    const renderCombo = (options, value, onChange, placeholder, disabled = false) => (
        <Autocomplete
            className={fieldStyles.combo}
            size="small"
            fullWidth
            disabled={disabled}
            options={options}
            value={options.find((o) => o.value === value) || null}
            isOptionEqualToValue={(o, v) => o?.value === v?.value}
            getOptionLabel={(o) => o?.label ?? ''}
            onChange={(_, v) => onChange(v ? v.value : '')}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="standard"
                    fullWidth
                    placeholder={placeholder}
                    className={`${fieldStyles.styleInput}${!value && !disabled ? ` ${fieldStyles.styleInputRequired}` : ''}`}
                    inputProps={{ ...params.inputProps, ...inputProps }}
                    InputProps={{ ...params.InputProps, ...comboInputProps }}
                />
            )}
            ListboxProps={{ direction }}
            PopperComponent={renderComboPopper}
        />
    );

    const renderAmountInput = (value, onChange, disabled = false) => (
        <TextField
            size="small"
            variant="standard"
            fullWidth
            type="number"
            disabled={disabled}
            className={`${fieldStyles.styleInput}${round2(value) === 0 && !disabled ? ` ${fieldStyles.styleInputRequired}` : ''}`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={t('Amount') || 'Amount'}
            InputProps={comboInputProps}
            inputProps={{ step: '0.01', style: { textAlign } }}
        />
    );

    const renderDescriptionInput = (value, onChange, placeholder, disabled = false) => (
        <TextField
            size="small"
            variant="standard"
            fullWidth
            disabled={disabled}
            className={fieldStyles.styleInput}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            InputProps={comboInputProps}
            inputProps={inputProps}
        />
    );

    const renderCategoryLines = () => (
        <div className={styles.side}>
            <div className={styles.warning}>
                {t('PayrollSingleSidedHint')}
            </div>
            <div className={modalStyles.tableWrap}>
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ '& .MuiTableCell-root': { py: 1, px: 1, textAlign, verticalAlign: 'middle' } }}
                >
                    <Table size="small" dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" className={modalStyles.actionsCell} />
                                <TableCell align={textAlign}>{t('PayrollCategory')}</TableCell>
                                <TableCell align={textAlign}>{t('SortCode') || 'Sort code'}</TableCell>
                                <TableCell align={textAlign} className={styles.amountCell}>{t('Amount') || 'Amount'}</TableCell>
                                <TableCell align={textAlign}>{t('Description') || 'Description'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {categoryLines.map((l) => {
                                const label = categoryLabel(l.key);
                                const disabled = !l.enabled || saving;
                                return (
                                    <TableRow key={l.key} hover>
                                        <TableCell align="center" className={modalStyles.actionsCell}>
                                            <Checkbox
                                                size="small"
                                                checked={l.enabled}
                                                onChange={(e) => updateCategoryLine(l.key, 'enabled', e.target.checked)}
                                                disabled={saving}
                                                inputProps={{ 'aria-label': label }}
                                            />
                                        </TableCell>
                                        <TableCell align={textAlign} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                            {label}
                                        </TableCell>
                                        <TableCell align={textAlign} sx={{ minWidth: 220 }}>
                                            {renderCombo(
                                                sortCodeOptions,
                                                l.sortCodeId,
                                                (value) => updateCategoryLine(l.key, 'sortCodeId', value),
                                                t('SortCode') || 'Sort code',
                                                disabled
                                            )}
                                        </TableCell>
                                        <TableCell align={textAlign}>
                                            {renderAmountInput(l.amount, (value) => updateCategoryLine(l.key, 'amount', value), disabled)}
                                        </TableCell>
                                        <TableCell align={textAlign} sx={{ minWidth: 200 }}>
                                            {renderDescriptionInput(
                                                l.description,
                                                (value) => updateCategoryLine(l.key, 'description', value),
                                                `${description} - ${label}`,
                                                disabled
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            <div className={styles.sideTotal}>{t('Total') || 'Total'}: {beautifyNumber(totalCategories)}</div>
        </div>
    );

    const renderSide = (title, lines, setLines, total) => {
        const onChange = updateLine(setLines);
        const onRemove = removeLine(setLines);
        return (
            <div className={styles.side}>
                <div className={modalStyles.toolbar}>
                    <p className={modalStyles.sectionTitle} style={{ margin: 0 }}>{title}</p>
                    <button
                        type="button"
                        className={modalStyles.ghostBtn}
                        onClick={() => setLines((prev) => [...prev, emptyLine()])}
                        disabled={saving}
                    >
                        <AddIcon fontSize="small" />
                        {t('AddLine') || 'Add line'}
                    </button>
                </div>
                <div className={modalStyles.tableWrap}>
                    <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ '& .MuiTableCell-root': { py: 1, px: 1, textAlign, verticalAlign: 'middle' } }}
                    >
                        <Table size="small" dir={direction}>
                            <TableHead>
                                <TableRow>
                                    <TableCell align={textAlign}>{t('AccountCard') || 'Account card'}</TableCell>
                                    <TableCell align={textAlign} className={styles.amountCell}>{t('Amount') || 'Amount'}</TableCell>
                                    <TableCell align={textAlign}>{t('Description') || 'Description'}</TableCell>
                                    <TableCell align="center" className={modalStyles.actionsCell} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lines.map((l) => (
                                    <TableRow key={l.key} hover>
                                        <TableCell align={textAlign} sx={{ minWidth: 200 }}>
                                            {renderCombo(
                                                cardOptions,
                                                l.account,
                                                (value) => onChange(l.key, 'account', value),
                                                t('AccountCard') || 'Account card'
                                            )}
                                        </TableCell>
                                        <TableCell align={textAlign}>
                                            {renderAmountInput(l.amount, (value) => onChange(l.key, 'amount', value))}
                                        </TableCell>
                                        <TableCell align={textAlign}>
                                            {renderDescriptionInput(
                                                l.description,
                                                (value) => onChange(l.key, 'description', value),
                                                description || t('Description') || 'Description'
                                            )}
                                        </TableCell>
                                        <TableCell align="center" className={modalStyles.actionsCell}>
                                            <Tooltip title={t('Delete') || 'Delete'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onRemove(l.key)}
                                                        disabled={lines.length === 1 || saving}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
                <div className={styles.sideTotal}>{t('Total') || 'Total'}: {beautifyNumber(total)}</div>
            </div>
        );
    };

    const isManual = tab === TAB_MANUAL;

    return (
        <Portal>
            <div
                className={`${modalStyles.overlay} ${direction === 'rtl' ? modalStyles.rtl : modalStyles.ltr}`}
                onClick={saving ? undefined : onClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={`${modalStyles.modal} ${isManual ? (isSingleSided ? styles.modalWide : styles.modalXL) : ''}`}>
                        <div className={modalStyles.body}>
                            <div className={modalStyles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={modalStyles.closeIcon} disabled={saving}>
                                        <CancelIcon className={modalStyles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t('PayrollJournalEntries') || 'פקודות שכר'}</div>
                                <div />
                            </div>

                            <div className={modalStyles.tabs} role="tablist">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={isManual}
                                    className={`${modalStyles.tab} ${isManual ? modalStyles.tabActive : ''}`}
                                    onClick={() => setTab(TAB_MANUAL)}
                                    disabled={saving}
                                >
                                    {t('ManualEntry') || 'Manual entry'}
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={!isManual}
                                    className={`${modalStyles.tab} ${!isManual ? modalStyles.tabActive : ''}`}
                                    onClick={() => setTab(TAB_IMPORT)}
                                    disabled={saving}
                                >
                                    {t('ImportFromMovinDat') || 'Import MovinDAT'}
                                </button>
                            </div>

                            <div className={modalStyles.scroll}>
                                {isManual ? (
                                    <>
                                        <div className={modalStyles.dateRow} dir={direction}>
                                            <div className={modalStyles.dateField}>
                                                <span className={modalStyles.fieldLabel}>{t('DocumentDate') || 'Document date'}</span>
                                                <CustomDatePicker
                                                    date={documentDate}
                                                    onDateChange={(d) => d && setDocumentDate(d)}
                                                    height="40px"
                                                    label=""
                                                />
                                            </div>
                                            <div className={modalStyles.dateField}>
                                                <span className={modalStyles.fieldLabel}>{t('MonthReportDate') || 'Reporting month'}</span>
                                                <CustomMonthDatePicker
                                                    date={reportingMonth}
                                                    onDateChange={(d) => d && setReportingMonth(d)}
                                                    height="40px"
                                                    width="100%"
                                                />
                                            </div>
                                            <div className={modalStyles.formField}>
                                                <span className={modalStyles.fieldLabel}>{t('Reference') || 'Reference'}</span>
                                                <TextField
                                                    size="small"
                                                    variant="standard"
                                                    fullWidth
                                                    className={fieldStyles.styleInput}
                                                    value={reference}
                                                    onChange={(e) => setReference(e.target.value)}
                                                    placeholder={t('Reference') || 'Reference'}
                                                    InputProps={comboInputProps}
                                                    inputProps={inputProps}
                                                />
                                            </div>
                                            <div className={`${modalStyles.formField} ${modalStyles.formFieldWide}`}>
                                                <span className={modalStyles.fieldLabel}>{t('Description') || 'Description'}</span>
                                                <TextField
                                                    size="small"
                                                    variant="standard"
                                                    fullWidth
                                                    className={fieldStyles.styleInput}
                                                    value={description}
                                                    onChange={(e) => setDescription(e.target.value)}
                                                    placeholder={t('Description') || 'Description'}
                                                    InputProps={comboInputProps}
                                                    inputProps={inputProps}
                                                />
                                            </div>
                                        </div>

                                        {isSingleSided ? renderCategoryLines() : (
                                            <>
                                                <div className={styles.sides}>
                                                    {renderSide(t('DebitSide') || 'Debit side', debitLines, setDebitLines, totalDebit)}
                                                    {renderSide(t('CreditSide') || 'Credit side', creditLines, setCreditLines, totalCredit)}
                                                </div>

                                                <div className={`${styles.balance} ${isBalanced ? styles.balanceOk : styles.balanceError}`}>
                                                    <span>{t('TotalDebit') || 'Total debit'}: {beautifyNumber(totalDebit)}</span>
                                                    <span>{t('TotalCredit') || 'Total credit'}: {beautifyNumber(totalCredit)}</span>
                                                    <span>{t('Difference') || 'Difference'}: {beautifyNumber(difference)}</span>
                                                    {!isBalanced && <span>{t('EntryNotBalanced') || 'The entry is not balanced'}</span>}
                                                </div>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <div className={styles.methods}>
                                        <button type="button" className={styles.methodCard} onClick={() => setImportOpen(true)}>
                                            <span className={styles.methodTitle}>
                                                <UploadFileIcon fontSize="small" />
                                                {t('ImportFromMovinDat') || 'Import MovinDAT'}
                                            </span>
                                            <p className={styles.methodDesc}>
                                                {t('PayrollImportHint') || 'Upload a MovinDAT file; all imported entries are tagged as payroll journal entries.'}
                                            </p>
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className={modalStyles.footer}>
                                <button type="button" className={modalStyles.secondaryBtn} onClick={onClose} disabled={saving}>
                                    {t('cancelButton') || 'Cancel'}
                                </button>
                                {isManual ? (
                                    <button
                                        type="button"
                                        className={modalStyles.primaryBtn}
                                        disabled={!canCreate}
                                        onClick={handleCreate}
                                    >
                                        {saving
                                            ? <CircularProgress className={modalStyles.spinner} size={18} color="inherit" />
                                            : (t('Create') || 'Create')}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className={modalStyles.primaryBtn}
                                        onClick={() => setImportOpen(true)}
                                    >
                                        {t('ChooseFile') || 'Choose file'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default PayrollJournalModal;
